import React, { useEffect, useState } from "react";
import axios from "axios";
import { useQuery, useMutation, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Eye,
  Trash2,
  Users,
  Plus,
  Download,
  Briefcase,
  MapPin,
  Calendar,
  DollarSign,
  Clock,
  Star,
  Sparkles,
  Building2,
  User,
  Mail,
  Phone,
  X,
  Check,
  AlertTriangle,
  RefreshCw,
  PhoneCall // Added PhoneCall icon
} from "lucide-react";
import { toast } from 'react-hot-toast';
import ModernSidebar from "./admin_sidebar";
import ModernHeader from "./modern-header";
import { ClipLoader } from "react-spinners";
import { tamilNaduSubdistricts } from '../utils/tamilNaduSubdistricts';
import { isGuest } from "../utils/isGuest";
import { useNavigate } from "react-router-dom";

const queryClient = new QueryClient();

// API functions for sector contacts
const getSectorContacts = async () => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/sector-contacts`);
    return response.data;
  } catch (error) {
    console.error('Error fetching sector contacts:', error);
    throw error;
  }
};

const getContactsBySector = async (sector) => {
  try {
    const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/sector-contacts/${sector}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching sector contacts:', error);
    throw error;
  }
};

const initialForm = {
  company: "",
  logo: "",
  isNew: false,
  featured: false,
  position: "",
  role: "",
  level: "",
  contract: "",
  district: "",
  salary: "",
  experience: "",
  languages: "",
  tools: "",
  shortDescription: "",
  longDescription: ""
};

const initialErrors = {
  company: "",
  position: "",
  role: "",
  level: "",
  contract: "",
  district: "",
  salary: "",
  experience: "",
  languages: "",
  tools: "",
  shortDescription: "",
  longDescription: ""
};

const districtsOfTamilNadu =  Object.keys(tamilNaduSubdistricts);

// Skeleton Loader Components
const JobCardSkeleton = () => (
  <div className="bg-white/70 backdrop-blur-sm border border-white/20 rounded-3xl p-6 shadow-lg">
    <div className="animate-pulse space-y-4">
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 bg-gray-200 rounded-2xl"></div>
        <div className="flex-1 space-y-2">
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          <div className="h-5 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
            <div className="space-y-1">
              <div className="h-3 bg-gray-200 rounded w-16"></div>
              <div className="h-4 bg-gray-200 rounded w-24"></div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
            <div className="space-y-1">
              <div className="h-3 bg-gray-200 rounded w-16"></div>
              <div className="h-4 bg-gray-200 rounded w-24"></div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
            <div className="space-y-1">
              <div className="h-3 bg-gray-200 rounded w-16"></div>
              <div className="h-4 bg-gray-200 rounded w-24"></div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
            <div className="space-y-1">
              <div className="h-3 bg-gray-200 rounded w-16"></div>
              <div className="h-4 bg-gray-200 rounded w-24"></div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <div className="h-10 bg-gray-200 rounded-xl flex-1"></div>
        <div className="h-10 bg-gray-200 rounded-xl flex-1"></div>
        <div className="h-10 bg-gray-200 rounded-xl flex-1"></div>
      </div>
    </div>
  </div>
);

const StatsCardSkeleton = () => (
  <div className="bg-white/70 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-lg">
    <div className="animate-pulse">
      <div className="flex items-center justify-between">
        <div>
          <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
          <div className="h-8 bg-gray-200 rounded w-16"></div>
        </div>
        <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
      </div>
    </div>
  </div>
);

const JobPortalInner = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);
  const navigate=useNavigate()
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState(initialErrors);
  const [editingId, setEditingId] = useState(null);
  const [showApplicationsModal, setShowApplicationsModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [startupProfile, setStartupProfile] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showExpertModal, setShowExpertModal] = useState(false); // New state for expert modal
  const [sectorContacts, setSectorContacts] = useState([]); // New state for sector contacts

  // Fetch jobs data with TanStack Query
  const { data: careers = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['jobs'],
    queryFn: async () => {
      const localUserId = localStorage.getItem('startupId');
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/jobs`);
      return res.data.filter(job => String(job.userId) === String(localUserId));
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Fetch applications for a specific job
  const { data: applications = [], isLoading: isLoadingApplications } = useQuery({
    queryKey: ['applications', selectedJobId],
    queryFn: async () => {
      if(isGuest()){
        return
      }
      if (!selectedJobId) return [];
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/jobapplied/${selectedJobId}`);
      return res.data;
    },
    enabled: !!selectedJobId,
  });

  // Fetch startup profile
  const { data: startupData } = useQuery({
    queryKey: ['startupProfile'],
    queryFn: async () => {
      const startupId = localStorage.getItem("startupId");
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/startups/${startupId}`);
      setStartupProfile(response.data);
      if (!editingId) {
        setFormData(prev => ({
          ...prev,
          logo: response.data.logoUrl || ""
        }));
      }
      return response.data;
    },
  });

  // Fetch sector contacts
  const { data: contactsData } = useQuery({
    queryKey: ['sectorContacts'],
    queryFn: async () => {
      try {
        const contacts = await getSectorContacts();
        setSectorContacts(contacts);
        return contacts;
      } catch (error) {
        console.error('Error fetching sector contacts:', error);
        return [];
      }
    },
  });

  // Mutations for CRUD operations
  const createJobMutation = useMutation({
    mutationFn: (payload) => axios.post(`${process.env.REACT_APP_API_URL}/api/jobs`, payload),
    onSuccess: () => {
      toast.success("Career added successfully!");
      queryClient.invalidateQueries(['jobs']);
      setFormData(initialForm);
      setShowModal(false);
      setShowConfirmationModal(false);
    },
    onError: (error) => {
      const msg = error.response?.data?.details || "Something went wrong";
      toast.error(msg);
    }
  });

  const updateJobMutation = useMutation({
    mutationFn: (payload) => axios.put(`${process.env.REACT_APP_API_URL}/api/jobs/${editingId}`, payload),
    onSuccess: () => {
      toast.success("Career updated successfully!");
      queryClient.invalidateQueries(['jobs']);
      setFormData(initialForm);
      setEditingId(null);
      setShowModal(false);
    },
    onError: (error) => {
      const msg = error.response?.data?.details || "Something went wrong";
      toast.error(msg);
    }
  });

  const deleteJobMutation = useMutation({
    mutationFn: (id) => axios.delete(`${process.env.REACT_APP_API_URL}/api/jobs/${id}`),
    // Optimistic update
    onMutate: async (id) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries(['jobs']);

      // Snapshot previous value
      const previousJobs = queryClient.getQueryData(['jobs']);

      // Optimistically remove job from UI
      queryClient.setQueryData(['jobs'], (oldJobs) => oldJobs.filter(job => job.id !== id));

      // Close modal immediately
      setShowDeleteModal(false);

      return { previousJobs };
    },
    onError: (err, id, context) => {
      // Revert back if error occurs
      queryClient.setQueryData(['jobs'], context.previousJobs);
      toast.error("Failed to delete job");
    },
    onSuccess: () => {
      toast.success("Job deleted successfully!");
    },
    onSettled: () => {
      queryClient.invalidateQueries(['jobs']); // Ensure data is fresh
    }
  });

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setSidebarOpen(false);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only close modal if click is outside modal container
      if (showModal && !event.target.closest('.modal-content')) {
        setShowModal(false);
      }
      if (showApplicationsModal && !event.target.closest('.applications-modal')) {
        setShowApplicationsModal(false);
      }
      if (showResumeModal && !event.target.closest('.resume-modal')) {
        setShowResumeModal(false);
      }
      if (showDeleteModal && !event.target.closest('.delete-modal')) {
        setShowDeleteModal(false);
      }
      if (showConfirmationModal && !event.target.closest('.confirmation-modal')) {
        setShowConfirmationModal(false);
      }
      if (showExpertModal && !event.target.closest('.expert-modal')) {
        setShowExpertModal(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [
    showModal,
    showApplicationsModal,
    showResumeModal,
    showDeleteModal,
    showConfirmationModal,
    showExpertModal,
  ]);


  const getResumeURL = () => {
    const url = selectedApplication?.resumeUrl || "";
    if (url.startsWith("http")) return url;
    return `${process.env.REACT_APP_API_URL}${url.startsWith("/") ? url : "/" + url}`;
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (name === "logo" && files.length > 0) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({ ...prev, logo: reader.result }));
      };
      reader.readAsDataURL(files[0]);
    } else {
      setFormData({ ...formData, [name]: type === "checkbox" ? checked : value });
    }
  };

  const downloadResume = async () => {
    try {
      let url = selectedApplication?.resumeUrl || "";

      // Ensure URL uses HTTPS if it's from your domain
      if (url.startsWith("http://api.tactos.in")) {
        url = url.replace("http://", "https://");
      }
      // If it's a relative path, prepend the API URL with HTTPS
      else if (url.startsWith("/")) {
        url = `https://api.tactos.in${url}`;
      }
      // If it doesn't start with http at all, assume it's a filename
      else if (!url.startsWith("http")) {
        url = `https://api.tactos.in/uploads/files/${url}`;
      }

      const fileName = url.split('/').pop() || 'resume.pdf';

      const response = await axios.get(url, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/pdf',
        }
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);

    } catch (error) {
      console.error('Failed to download resume:', error);
      toast.error('Error downloading resume. Please try again.');
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = { ...initialErrors };

    const requiredFields = [
      'company', 'position', 'role', 'level',
      'contract', 'district', 'salary', 'experience',
      'shortDescription', 'longDescription'
    ];

    requiredFields.forEach(field => {
      if (!formData[field]) {
        newErrors[field] = "This field is required";
        isValid = false;
      }
    });

    const numericFields = ['salary', 'experience'];
    numericFields.forEach(field => {
      if (formData[field] && isNaN(formData[field])) {
        newErrors[field] = "Must be a number";
        isValid = false;
      }
    });

    if (!formData.languages) {
      newErrors.languages = "At least one language is required";
      isValid = false;
    }

    if (!formData.tools) {
      newErrors.tools = "At least one tool is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setShowConfirmationModal(true);
  };

  const confirmJobCreation = async () => {
    const userId = localStorage.getItem("startupId");
    if (!userId) {
      toast.error("User not logged in.");
      return;
    }

    const payload = {
      ...formData,
      userId,
      languages: formData.languages.split(",").map((x) => x.trim()),
      tools: formData.tools.split(",").map((x) => x.trim()),
    };

    if (editingId) {
      updateJobMutation.mutate(payload);
    } else {
      createJobMutation.mutate(payload);
    }
  };

  const fetchApplications = (jobId) => {
    setSelectedJobId(jobId);
    setShowApplicationsModal(true);
  };

  const handleView = (career) => {
    setFormData({
      ...career,
      languages: career.languages.join(", "),
      tools: career.tools.join(", "),
    });
    setEditingId(career._id);
    setShowConfirmationModal(true);
  };

  const handleDeleteClick = (id) => {
    setJobToDelete(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    console.log(jobToDelete);
    if (jobToDelete) {
      deleteJobMutation.mutate(jobToDelete);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setJobToDelete(null);
  };

  const handlePreviewResume = (application) => {
    setSelectedApplication(application);
    setShowResumeModal(true);
  };

  // Function to handle calling an expert
  const handleCallExpert = (phoneNumber) => {
    // Clean the phone number - remove any non-digit characters except '+'
    const cleanedPhoneNumber = phoneNumber.replace(/[^\d+]/g, '');

    // Open the phone dialer with the cleaned number
    window.location.href = `tel:${cleanedPhoneNumber}`;
  };

  const getSectorPhoneNumber = () => {
    if (!startupProfile || !startupProfile.sector || !sectorContacts || Object.keys(sectorContacts).length === 0) return null;

    const startupSector = startupProfile.sector.toLowerCase().trim();

    const sectorKey = Object.keys(sectorContacts).find(
      key => key.toLowerCase().includes(startupSector) || startupSector.includes(key.toLowerCase())
    );

    if (sectorKey && sectorContacts[sectorKey] && sectorContacts[sectorKey].length > 0) {
      return sectorContacts[sectorKey][0];
    }

    if (sectorContacts["Job-Portal"] && sectorContacts["Job-Portal"].length > 0) {
      return sectorContacts["Job-Portal"][0];
    }

    return null;
  };

  const getAnyContactNumber = () => {
    if (!sectorContacts || Object.keys(sectorContacts).length === 0) return null;

    if (sectorContacts["Job-Portal"] && sectorContacts["Job-Portal"].length > 0) {
      return sectorContacts["Job-Portal"][0];
    }

    const firstSector = Object.keys(sectorContacts)[0];
    if (sectorContacts[firstSector] && sectorContacts[firstSector].length > 0) {
      return sectorContacts[firstSector][0];
    }

    return null;
  };

  // Function to check if there are any contacts available
  const hasContacts = () => {
    return sectorContacts && Object.keys(sectorContacts).length > 0 &&
      Object.values(sectorContacts).some(numbers => numbers && numbers.length > 0);
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <ModernSidebar
        isOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        isMobile={isMobile}
        fullName={fullName}
        email={email}
      />

      <div className={`flex flex-col flex-1 transition-all duration-300 ${isMobile ? "ml-0" : "ml-16 sm:ml-18 md:ml-20 lg:ml-20 xl:ml-20"}`}>
        <ModernHeader toggleSidebar={toggleSidebar} isMobile={isMobile} />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="px-4 space-y-8">
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-black via-gray-900 to-slate-600 bg-clip-text text-transparent">
                  Job Management
                </h1>
                <p className="text-gray-600 mt-2">Create and manage your job postings</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowExpertModal(true)}
                  className="group relative overflow-hidden bg-black hover:bg-gray-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-3"
                >
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-200"></div>
                  <PhoneCall className="w-5 h-5" />
                  <span>Call to Expert</span>
                </button>
                <button
                onClick={() => {
                   if(isGuest()){
                    toast.error("Please login to post a job")
                    navigate("/login")
                   }else{
                     setFormData(initialForm)
                    setEditingId(null)
                    setShowModal(true)
                   }
                  }}
                  className="group relative overflow-hidden bg-black hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-3"
                >
                  <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-200"></div>
                  <Plus className="w-5 h-5" />
                  <span>Create New Job</span>
                </button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {isLoading ? (
                <>
                  <StatsCardSkeleton />
                  <StatsCardSkeleton />
                  <StatsCardSkeleton />
                </>
              ) : (
                <>
                  <div className="bg-white/70 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-600 text-sm font-medium">Total Jobs</p>
                        <p className="text-3xl font-bold text-gray-900">{careers.length}</p>
                      </div>
                      <div className="bg-blue-100 p-3 rounded-xl">
                        <Briefcase className="w-6 h-6 text-blue-600" />
                      </div>
                    </div>
                  </div>
                  <div className="bg-white/70 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-600 text-sm font-medium">Featured Jobs</p>
                        <p className="text-3xl font-bold text-gray-900">{careers.filter((job) => job.featured).length}</p>
                      </div>
                      <div className="bg-yellow-100 p-3 rounded-xl">
                        <Star className="w-6 h-6 text-yellow-600" />
                      </div>
                    </div>
                  </div>
                  <div className="bg-white/70 backdrop-blur-sm border border-white/20 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-600 text-sm font-medium">New Jobs</p>
                        <p className="text-3xl font-bold text-gray-900">{careers.filter((job) => job.isNew).length}</p>
                      </div>
                      <div className="bg-green-100 p-3 rounded-xl">
                        <Sparkles className="w-6 h-6 text-green-600" />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Jobs Grid */}
            {isLoading ? (
              <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-8">
                <JobCardSkeleton />
                <JobCardSkeleton />
                <JobCardSkeleton />
              </div>
            ) : isError ? (
              <div className="flex flex-col items-center justify-center py-20 bg-white/50 backdrop-blur-sm rounded-3xl border border-white/20 shadow-lg">
                <div className="bg-red-100 p-6 rounded-full mb-6">
                  <AlertTriangle className="w-16 h-16 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3">Error Loading Jobs</h3>
                <p className="text-gray-600 text-center max-w-md mb-8">
                  Failed to fetch job postings. Please try again.
                </p>
                <button
                  onClick={() => refetch()}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-3"
                >
                  <RefreshCw className="w-5 h-5" />
                  Retry
                </button>
              </div>
            ) : careers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 bg-white/50 backdrop-blur-sm rounded-3xl border border-white/20 shadow-lg">
                <div className="bg-gradient-to-br from-blue-100 to-purple-100 p-6 rounded-full mb-6">
                  <Briefcase className="w-16 h-16 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3">No Jobs Posted Yet</h3>
                <p className="text-gray-600 text-center max-w-md mb-8">
                  Start building your team by creating your first job posting. Attract top talent with compelling job
                  descriptions.
                </p>
                <button
                  onClick={() => {
                   if(isGuest()){
                    toast.error("Please login to post a job")
                    navigate("/login")
                   }else{
                     setFormData(initialForm)
                    setEditingId(null)
                    setShowModal(true)
                   }
                  }}
                  className="bg-black hover:from-blue-700 hover:to-purple-700 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-3"
                >
                  <Plus className="w-5 h-5" />
                  Create Your First Job
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
                {careers.map((career) => {
                  // compute inside the map callback
                  const createdAtMs = new Date(career.createdAt).getTime();
                  const isNewTag = Date.now() - createdAtMs < 24 * 60 * 60 * 1000;

                  return (
                    <div
                      key={career._id}
                      className="group relative bg-white/70 backdrop-blur-sm border border-white/20 rounded-3xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-[1.02]"
                    >
                      {/* Status Badges */}
                      <div className="absolute top-4 right-4 flex gap-2">
                        {isNewTag && (
                          <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                            NEW
                          </span>
                        )}
                        {career.featured && (
                          <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg flex items-center gap-1">
                            <Star className="w-3 h-3" />
                            FEATURED
                          </span>
                        )}
                      </div>

                      {/* Company Header */}
                      <div className="flex items-start gap-4 mb-6">
                        <div className="relative">
                          <img
                            src={career.logo || "/placeholder.svg"}
                            alt="logo"
                            className="w-16 h-16 object-contain border-2 border-white rounded-2xl shadow-lg bg-white p-2"
                          />
                          <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full border-2 border-white"></div>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-1 mt-3">{career.company}</h3>
                          <p className="text-lg font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                            {career.position}
                          </p>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleView(career)}
                          className="flex-1 bg-black text-white font-semibold py-2 px-2 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2"
                        >
                          <Eye className="w-4 h-4" />
                          <span className="hidden sm:inline">View</span>
                        </button>
                        <button
                          onClick={() => fetchApplications(career._id)}
                          className="flex-1 bg-black text-white font-semibold py-2 px-2 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2"
                        >
                          <Users className="w-4 h-4" />
                          <span className="hidden sm:inline">Applications</span>
                        </button>
                        <button
                          onClick={() => handleDeleteClick(career._id)}
                          className="flex-1 bg-black text-white font-semibold py-2 px-2 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span className="hidden sm:inline">Delete</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Expert Modal */}
            {showExpertModal && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-[9999] p-4">
                <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 relative transform animate-in zoom-in-95 duration-300 expert-modal">
                  <div className="absolute top-4 right-4">
                    <button
                      onClick={() => setShowExpertModal(false)}
                      className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors duration-200"
                    >
                      <X className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>

                  <div className="text-center mb-6">
                    <div className="bg-gradient-to-br from-blue-100 to-purple-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                      <PhoneCall className="w-8 h-8 text-blue-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Call Our Job Portal Expert</h2>
                    <p className="text-gray-600">
                      Get personalized assistance with your job postings and recruitment needs.
                    </p>
                  </div>

                  {hasContacts() ? (
                    <>
                      {startupData && startupData.sector && getSectorPhoneNumber() ? (
                        <div className="bg-blue-50 rounded-xl p-5 mb-6">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">Sector-Specific Support</h3>
                          <p className="text-gray-700 mb-3">
                            Your startup is in the <span className="font-semibold">{startupData.sector}</span> sector.
                            Contact our dedicated expert for specialized assistance.
                          </p>
                          <div className="flex items-center justify-center gap-2 text-lg font-semibold text-blue-700">
                            <Phone className="w-5 h-5" />
                            <span>{getSectorPhoneNumber()}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="bg-purple-50 rounded-xl p-5 mb-6">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">General Support</h3>
                          <p className="text-gray-700 mb-3">
                            Contact our job portal experts for assistance with your recruitment needs.
                          </p>
                          <div className="flex items-center justify-center gap-2 text-lg font-semibold text-purple-700">
                            <Phone className="w-5 h-5" />
                            <span>{getAnyContactNumber()}</span>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="bg-gray-50 rounded-xl p-5 mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Support Unavailable</h3>
                      <p className="text-gray-700 mb-3">
                        Our support team contact information is currently being updated.
                      </p>
                      <div className="text-center">
                        <p className="text-sm text-gray-600">
                          Please check back later or email us at support@tactos.in
                        </p>
                      </div>
                    </div>
                  )}

                  {hasContacts() && (
                    <button
                      onClick={() => handleCallExpert(getSectorPhoneNumber() || getAnyContactNumber())}
                      className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-3"
                    >
                      <PhoneCall className="w-5 h-5" />
                      Call Now
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Resume Download Modal */}
            {showResumeModal && selectedApplication && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-[9999] p-4">
                <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 relative transform animate-in zoom-in-95 duration-300">
                  <div className="absolute top-4 right-4">
                    <button
                      onClick={() => setShowResumeModal(false)}
                      className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors duration-200"
                    >
                      <X className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>

                  <div className="text-center mb-6">
                    <div className="bg-gradient-to-br from-blue-100 to-purple-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                      <Download className="w-8 h-8 text-blue-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Download Resume</h2>
                    <p className="text-gray-600">Click the button below to download the applicant's resume.</p>
                  </div>

                  <button
                    onClick={downloadResume}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-3"
                  >
                    <Download className="w-5 h-5" />
                    Download PDF Resume
                  </button>
                </div>
              </div>
            )}

            {/* Applications Modal */}
            {showApplicationsModal && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-start pt-10 z-50 p-4 applications-modal">
                <div className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden transform animate-in slide-in-from-top-4 duration-300">
                  <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
                    <div className="flex justify-between items-center">
                      <div>
                        <h2 className="text-2xl font-bold mb-1">Job Applications</h2>
                        <p className="text-blue-100">Review and manage candidate applications</p>
                      </div>
                      <button
                        onClick={() => setShowApplicationsModal(false)}
                        className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors duration-200"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                    {isLoadingApplications ? (
                      <div className="space-y-4">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="bg-gradient-to-r from-gray-50 to-blue-50 border border-gray-200 rounded-2xl p-6">
                            <div className="animate-pulse space-y-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                  <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                                  <div className="space-y-2">
                                    <div className="h-5 bg-gray-200 rounded w-32"></div>
                                    <div className="flex gap-4">
                                      <div className="h-4 bg-gray-200 rounded w-24"></div>
                                      <div className="h-4 bg-gray-200 rounded w-24"></div>
                                    </div>
                                  </div>
                                </div>
                                <div className="h-10 bg-gray-200 rounded-xl w-40"></div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : applications.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="bg-gray-100 p-6 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                          <Users className="w-10 h-10 text-gray-400" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">No Applications Yet</h3>
                        <p className="text-gray-500">This job hasn't received any applications yet.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {applications.map((application) => (
                          <div
                            key={application._id}
                            className="bg-gradient-to-r from-gray-50 to-blue-50 border border-gray-200 rounded-2xl p-6 hover:shadow-lg transition-all duration-300"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <div className="bg-gradient-to-br from-blue-500 to-purple-500 p-3 rounded-full">
                                  <User className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                  <h3 className="text-xl font-bold text-gray-900">{application.name}</h3>
                                  <div className="flex items-center gap-4 mt-1">
                                    <div className="flex items-center gap-1 text-gray-600">
                                      <Mail className="w-4 h-4" />
                                      <span className="text-sm">{application.email}</span>
                                    </div>
                                    <div className="flex items-center gap-1 text-gray-600">
                                      <Phone className="w-4 h-4" />
                                      <span className="text-sm">{application.phone}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => handlePreviewResume(application)}
                                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                              >
                                <Download className="w-4 h-4" />
                                Download Resume
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Job Form Modal */}
            {showModal && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-start pt-4 z-50 p-4 modal-content">
                <div className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-hidden transform animate-in slide-in-from-top-4 duration-300">
                  <div className="bg-gradient-to-r from-black via-gray-900 to-slate-700 p-6 text-white">
                    <div className="flex justify-between items-center">
                      <div>
                        <h2 className="text-2xl font-bold mb-1">Create New Job</h2>
                        <p className="text-blue-100">
                          Fill in the details to create a new job posting
                        </p>
                      </div>
                      <button
                        onClick={() => setShowModal(false)}
                        className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors duration-200"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <div className="p-6 overflow-y-auto max-h-[calc(95vh-120px)]">
                    <form onSubmit={handleSubmit} className="space-y-8">
                      {/* Basic Information */}
                      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <Building2 className="w-5 h-5 text-blue-600" />
                          Basic Information
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {[
                            ["company", "Company Name", "text"],
                            ["position", "Job Position", "text"],
                            ["role", "Job Role", "text"],
                            ["salary", "Annual Salary (₹)", "text"],
                          ].map(([name, label, type]) => (
                            <div key={name}>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
                              <input
                                type={type}
                                name={name}
                                value={formData[name]}
                                onChange={handleChange}
                                className={`w-full border-2 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 ${errors[name] ? "border-red-500 bg-red-50" : "border-gray-200 bg-white"
                                  }`}
                                placeholder={`Enter ${label.toLowerCase()}`}
                              />
                              {errors[name] && (
                                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                                  <AlertTriangle className="w-4 h-4" />
                                  {errors[name]}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Job Details */}
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <Briefcase className="w-5 h-5 text-green-600" />
                          Job Details
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Experience Required
                            </label>
                            <input
                              type="text"
                              name="experience"
                              value={formData.experience}
                              onChange={handleChange}
                              className={`w-full border-2 rounded-xl px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 ${errors.experience ? "border-red-500 bg-red-50" : "border-gray-200 bg-white"
                                }`}
                              placeholder="Enter years of experience"
                            />
                            {errors.experience && (
                              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                                <AlertTriangle className="w-4 h-4" />
                                {errors.experience}
                              </p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">District</label>
                            <select
                              name="district"
                              value={formData.district}
                              onChange={handleChange}
                              className={`w-full border-2 rounded-xl px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 ${errors.district ? "border-red-500 bg-red-50" : "border-gray-200 bg-white"
                                }`}
                            >
                              <option value="">Select District</option>
                              {districtsOfTamilNadu.map((district) => (
                                <option key={district} value={district}>
                                  {district}
                                </option>
                              ))}
                            </select>
                            {errors.district && (
                              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                                <AlertTriangle className="w-4 h-4" />
                                {errors.district}
                              </p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Job Level</label>
                            <select
                              name="level"
                              value={formData.level}
                              onChange={handleChange}
                              className={`w-full border-2 rounded-xl px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 ${errors.level ? "border-red-500 bg-red-50" : "border-gray-200 bg-white"
                                }`}
                            >
                              <option value="">Select Level</option>
                              <option value="Senior">Senior</option>
                              <option value="Intermediate">Intermediate</option>
                              <option value="Fresher">Fresher</option>
                            </select>
                            {errors.level && (
                              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                                <AlertTriangle className="w-4 h-4" />
                                {errors.level}
                              </p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Contract Type</label>
                            <select
                              name="contract"
                              value={formData.contract}
                              onChange={handleChange}
                              className={`w-full border-2 rounded-xl px-4 py-3 focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 ${errors.contract ? "border-red-500 bg-red-50" : "border-gray-200 bg-white"
                                }`}
                            >
                              <option value="">Select Contract Type</option>
                              <option value="Full Time">Full Time</option>
                              <option value="Part Time">Part Time</option>
                              <option value="Internship">Internship</option>
                            </select>
                            {errors.contract && (
                              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                                <AlertTriangle className="w-4 h-4" />
                                {errors.contract}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Skills & Tools */}
                      <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <Star className="w-5 h-5 text-orange-600" />
                          Skills & Tools
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Required Languages</label>
                            <input
                              type="text"
                              name="languages"
                              value={formData.languages}
                              onChange={handleChange}
                              className={`w-full border-2 rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 ${errors.languages ? "border-red-500 bg-red-50" : "border-gray-200 bg-white"
                                }`}
                              placeholder="e.g., JavaScript, Python, Java (comma-separated)"
                            />
                            {errors.languages && (
                              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                                <AlertTriangle className="w-4 h-4" />
                                {errors.languages}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Required Tools</label>
                            <input
                              type="text"
                              name="tools"
                              value={formData.tools}
                              onChange={handleChange}
                              className={`w-full border-2 rounded-xl px-4 py-3 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 ${errors.tools ? "border-red-500 bg-red-50" : "border-gray-200 bg-white"
                                }`}
                              placeholder="e.g., React, Node.js, Docker (comma-separated)"
                            />
                            {errors.tools && (
                              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                                <AlertTriangle className="w-4 h-4" />
                                {errors.tools}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Job Description */}
                      <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-2xl p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Job Description</h3>
                        <div className="space-y-6">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Short Description</label>
                            <textarea
                              name="shortDescription"
                              value={formData.shortDescription}
                              onChange={handleChange}
                              className={`w-full border-2 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 ${errors.shortDescription ? "border-red-500 bg-red-50" : "border-gray-200 bg-white"
                                }`}
                              rows={3}
                              placeholder="Brief overview of the job (1-2 sentences)"
                            />
                            {errors.shortDescription && (
                              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                                <AlertTriangle className="w-4 h-4" />
                                {errors.shortDescription}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Detailed Description
                            </label>
                            <textarea
                              name="longDescription"
                              value={formData.longDescription}
                              onChange={handleChange}
                              className={`w-full border-2 rounded-xl px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 ${errors.longDescription ? "border-red-500 bg-red-50" : "border-gray-200 bg-white"
                                }`}
                              rows={6}
                              placeholder="Full job description including responsibilities, qualifications, benefits, etc."
                            />
                            {errors.longDescription && (
                              <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                                <AlertTriangle className="w-4 h-4" />
                                {errors.longDescription}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Additional Options */}
                      <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-2xl p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Options</h3>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Company Logo</label>
                            {formData.logo ? (
                              <div className="flex items-center gap-4 mb-4">
                                <img
                                  src={formData.logo}
                                  alt="Company logo"
                                  className="w-16 h-16 object-contain border rounded-lg"
                                />
                                <button
                                  type="button"
                                  onClick={() => setFormData(prev => ({ ...prev, logo: "" }))}
                                  className="text-red-600 hover:text-red-800 text-sm font-medium"
                                >
                                  Remove
                                </button>
                              </div>
                            ) : null}
                            <input
                              type="file"
                              name="logo"
                              accept="image/*"
                              onChange={handleChange}
                              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-all duration-200"
                            />
                            {startupProfile?.logoUrl && !formData.logo && (
                              <div className="mt-2">
                                <button
                                  type="button"
                                  onClick={() => setFormData(prev => ({ ...prev, logo: startupProfile.logoUrl }))}
                                  className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
                                >
                                  <RefreshCw className="w-4 h-4" />
                                  Use default company logo
                                </button>
                              </div>
                            )}
                          </div>
                          <div className="flex gap-6">
                            <label className="flex items-center gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                name="isNew"
                                checked={formData.isNew}
                                onChange={handleChange}
                                className="w-5 h-5 text-green-600 border-2 border-gray-300 rounded focus:ring-green-500"
                              />
                              <span className="text-sm font-semibold text-gray-700">Mark as New Job</span>
                            </label>
                            <label className="flex items-center gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                name="featured"
                                checked={formData.featured}
                                onChange={handleChange}
                                className="w-5 h-5 text-yellow-600 border-2 border-gray-300 rounded focus:ring-yellow-500"
                              />
                              <span className="text-sm font-semibold text-gray-700">Featured Job</span>
                            </label>
                          </div>
                        </div>
                      </div>

                      {/* Submit Button */}
                      <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={() => setShowModal(false)}
                          className="px-8 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all duration-200"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={createJobMutation.isPending}
                          className="px-8 py-3 bg-black hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                          {createJobMutation.isPending ? (
                            <ClipLoader size={20} color="#ffffff" />
                          ) : (
                            <>
                              <Check className="w-5 h-5" />
                              Review & Submit
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {/* Confirmation Modal */}
            {showConfirmationModal && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-[9999] p-4 confirmation-modal">
                <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden transform animate-in zoom-in-95 duration-300">
                  <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
                    <div className="flex justify-between items-center">
                      <div>
                        <h2 className="text-2xl font-bold mb-1">Confirm Job Details</h2>
                        <p className="text-blue-100">
                          Please review all the details before submitting
                        </p>
                      </div>
                      <button
                        onClick={() => setShowConfirmationModal(false)}
                        className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors duration-200"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  <div className="p-6 overflow-y-auto max-h-[calc(95vh-120px)]">
                    <div className="space-y-8">
                      {/* Company Header */}
                      <div className="flex items-start gap-6 mb-8">
                        {formData.logo && (
                          <img
                            src={formData.logo}
                            alt="logo"
                            className="w-20 h-20 object-contain border-2 border-white rounded-2xl shadow-lg bg-white p-2"
                          />
                        )}
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900 mb-2">{formData.company}</h3>
                          <p className="text-xl font-semibold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                            {formData.position}
                          </p>
                          <div className="flex gap-3 mt-3">
                            {formData.isNew && (
                              <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                                NEW
                              </span>
                            )}
                            {formData.featured && (
                              <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg flex items-center gap-1">
                                <Star className="w-3 h-3" />
                                FEATURED
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Job Details Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                        <div className="bg-blue-50 rounded-xl p-5">
                          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <Briefcase className="w-5 h-5 text-blue-600" />
                            Job Information
                          </h4>
                          <div className="space-y-4">
                            <div>
                              <p className="text-sm text-gray-500 font-medium">Role</p>
                              <p className="text-base font-semibold text-gray-900">{formData.role}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500 font-medium">Level</p>
                              <p className="text-base font-semibold text-gray-900">{formData.level}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500 font-medium">Contract Type</p>
                              <p className="text-base font-semibold text-gray-900">{formData.contract}</p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-green-50 rounded-xl p-5">
                          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <MapPin className="w-5 h-5 text-green-600" />
                            Location & Compensation
                          </h4>
                          <div className="space-y-4">
                            <div>
                              <p className="text-sm text-gray-500 font-medium">District</p>
                              <p className="text-base font-semibold text-gray-900">{formData.district}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500 font-medium">Annual Salary</p>
                              <p className="text-base font-semibold text-gray-900">₹{formData.salary}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500 font-medium">Experience Required</p>
                              <p className="text-base font-semibold text-gray-900">{formData.experience} years</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Skills & Tools */}
                      <div className="mb-8">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <Star className="w-5 h-5 text-orange-600" />
                          Required Skills
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="bg-orange-50 rounded-xl p-5">
                            <h5 className="text-sm font-semibold text-gray-700 mb-3">Languages</h5>
                            <div className="flex flex-wrap gap-2">
                              {formData.languages.split(",").map((lang, i) => (
                                <span
                                  key={i}
                                  className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-sm"
                                >
                                  {lang.trim()}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="bg-red-50 rounded-xl p-5">
                            <h5 className="text-sm font-semibold text-gray-700 mb-3">Tools & Technologies</h5>
                            <div className="flex flex-wrap gap-2">
                              {formData.tools.split(",").map((tool, i) => (
                                <span
                                  key={i}
                                  className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-semibold shadow-sm"
                                >
                                  {tool.trim()}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Job Description */}
                      <div className="mb-8">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <Briefcase className="w-5 h-5 text-indigo-600" />
                          Job Description
                        </h4>
                        <div className="space-y-6">
                          <div className="bg-indigo-50 rounded-xl p-5">
                            <h5 className="text-sm font-semibold text-gray-700 mb-3">Short Description</h5>
                            <p className="text-gray-800">{formData.shortDescription}</p>
                          </div>
                          <div className="bg-blue-50 rounded-xl p-5">
                            <h5 className="text-sm font-semibold text-gray-700 mb-3">Detailed Description</h5>
                            <p className="text-gray-800 whitespace-pre-line">{formData.longDescription}</p>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
                        <button
                          type="button"
                          onClick={() => setShowConfirmationModal(false)}
                          className="px-8 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all duration-200"
                        >
                          Back to Edit
                        </button>
                        <button
                          onClick={confirmJobCreation}
                          disabled={createJobMutation.isPending}
                          className="px-8 py-3 bg-black hover:from-green-700 hover:to-emerald-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                          {createJobMutation.isPending ? (
                            <ClipLoader size={20} color="#ffffff" />
                          ) : (
                            <>
                              <Check className="w-5 h-5" />
                              Confirm & Submit
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-[9999] p-4 delete-modal">
                <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 relative transform animate-in zoom-in-95 duration-300">
                  <div className="text-center mb-6">
                    <div className="bg-red-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                      <AlertTriangle className="w-8 h-8 text-red-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Delete Job Posting</h2>
                    <p className="text-gray-600">
                      Are you sure you want to delete this job posting? This action cannot be undone and all
                      applications will be lost.
                    </p>
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={handleCancelDelete}
                      disabled={deleteJobMutation.isPending}
                      className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleConfirmDelete}
                      disabled={deleteJobMutation.isPending}
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {deleteJobMutation.isPending ? (
                        <ClipLoader size={20} color="#ffffff" />
                      ) : (
                        "Delete Job"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

const JobPortal = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <JobPortalInner />
    </QueryClientProvider>
  );
};

export default JobPortal;